import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Expandable activity-timeline row for a `category === 'call'` entry
 * (§22.1 / #4 ticket activity timeline).
 *
 * Renders the standard one-liner the rest of the feed uses, plus a ▶
 * chevron that expands to show full call details — notes, contact name +
 * phone, duration — that previously were saved but never surfaced.
 *
 * Resolution path (in order):
 *   1. activity item carries `prescreen_call_id` (audit-payload field added
 *      in this fix) → exact match by id from the cached calls list.
 *   2. Legacy rows logged before the field landed → fall back to
 *      match-by-occurred_at within ±60s (audit time is stamped at the same
 *      transaction as the row, so the drift is small but non-zero).
 *   3. No match (call deleted somehow OR cache empty) → show a friendly
 *      "Details unavailable" notice; never crash the whole tab.
 *
 * Reuses the SAME TanStack Query cache key as PrescreeningCallsCard so we
 * don't double-fetch.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { hasPerm } from '@/store/auth';
import { PrescreeningCallForm } from './PrescreeningCallForm';
const OUTCOME_LABEL = {
    reached: 'Customer reached',
    no_answer: 'No answer',
    voicemail: 'Voicemail left',
    callback_scheduled: 'Callback scheduled',
    declined: 'Declined',
    other: 'Other',
};
// Same as backend EDIT_WINDOW_MINUTES; the backend is authoritative.
const EDIT_WINDOW_MS = 5 * 60 * 1000;
// Tolerance for legacy match-by-occurred_at. Audit row + call row are
// written in the same transaction so the drift is well under a second in
// practice — 60s is generous belt-and-braces.
const MATCH_TOLERANCE_MS = 60_000;
function findCall(calls, prescreenCallId, occurredAt) {
    if (prescreenCallId) {
        const match = calls.find((c) => c.id === prescreenCallId);
        if (match)
            return match;
    }
    // Legacy fallback: nearest occurred_at within tolerance.
    const target = new Date(occurredAt).getTime();
    let best = null;
    let bestDelta = Infinity;
    for (const c of calls) {
        const delta = Math.abs(new Date(c.occurred_at).getTime() - target);
        if (delta < bestDelta && delta <= MATCH_TOLERANCE_MS) {
            best = c;
            bestDelta = delta;
        }
    }
    return best;
}
export function CallActivityRow({ ticketId, item, me }) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    // Shares the cache key with PrescreeningCallsCard — no double fetch.
    // Enabled only when the row is expanded so we don't trigger the GET on
    // mount for every row of every activity feed.
    const calls = useQuery({
        queryKey: ['ticket', ticketId, 'prescreen-calls'],
        queryFn: () => api.get(`/tickets/${ticketId}/prescreen-calls`),
        staleTime: 60_000,
        enabled: expanded,
    });
    const call = expanded
        ? findCall(calls.data ?? [], item.prescreen_call_id, item.occurred_at)
        : null;
    const canEdit = !!call &&
        hasPerm(me, 'ticket.comment.internal') &&
        !!me?.user?.id &&
        call.conducted_by_user_id === me.user.id &&
        Date.now() - new Date(call.created_at).getTime() <= EDIT_WINDOW_MS;
    return (_jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsx("div", { className: "text-sm font-medium text-slate-700 dark:text-slate-300", children: item.summary }), _jsx("button", { type: "button", onClick: () => setExpanded((v) => !v), className: "shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800", "aria-label": expanded ? 'Hide call details' : 'Show call details', "aria-expanded": expanded, children: expanded ? '▲' : '▼' })] }), expanded && (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-900/40", children: [calls.isLoading && _jsx("div", { className: "text-slate-500", children: "Loading call details\u2026" }), calls.isError && (_jsx("div", { className: "text-rose-600", children: "Could not load call details. Try refreshing." })), !calls.isLoading && !calls.isError && !call && (_jsx("div", { className: "italic text-slate-500", children: "Details unavailable for this call (it may have been removed from the data set)." })), call && !editing && (_jsxs("div", { className: "space-y-1.5", children: [_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Called by:" }), ' ', _jsx("span", { className: "text-slate-700 dark:text-slate-200", children: call.conducted_by_display_name || call.conducted_by_email || '—' })] }), _jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Outcome:" }), ' ', _jsx("span", { className: "font-medium text-slate-700 dark:text-slate-200", children: OUTCOME_LABEL[call.outcome] ?? call.outcome })] }), call.duration_minutes != null && (_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Duration:" }), ' ', _jsxs("span", { className: "text-slate-700 dark:text-slate-200", children: [call.duration_minutes, " min"] })] })), call.contact_name && (_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Contact name:" }), ' ', _jsx("span", { className: "text-slate-700 dark:text-slate-200", children: call.contact_name })] })), call.contact_phone && (_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Phone:" }), ' ', _jsx("span", { className: "text-slate-700 dark:text-slate-200", children: call.contact_phone })] })), call.notes && (_jsxs("div", { children: [_jsx("div", { className: "text-slate-500", children: "Notes:" }), _jsx("div", { className: "whitespace-pre-wrap text-slate-700 dark:text-slate-200", children: call.notes })] })), !call.notes && !call.contact_name && !call.contact_phone && (_jsx("div", { className: "italic text-slate-400", children: "No additional details captured." })), canEdit && (_jsx("div", { className: "pt-1", children: _jsx(Button, { size: "sm", variant: "outline", onClick: () => setEditing(true), children: "\u270F Edit (within 5 min)" }) }))] })), call && editing && (_jsx(PrescreeningCallForm, { ticketId: ticketId, existingCall: call, alwaysOpen: true, onDone: () => setEditing(false) }))] }))] }));
}
